import { Button } from "@/components/ui/button";
import { Mail, User } from "lucide-react";
import type { Booking } from "@/types/Booking";
import type { Member } from "@/types/Member";

type SlotBookingRestrictionProps = {
  booking: Booking;
  onClose?: () => void;
};

const SlotBookingRestriction = ({ booking, onClose }: SlotBookingRestrictionProps) => {
  // Get practice manager info from the booking location
  const practiceManager = booking.location?.manager?.[0] as unknown as Member | undefined;

  return (
    <div className="min-h-[90vh] lg:min-h-[70vh] w-full flex flex-col bg-white rounded-xl my-4">
      {/* Header - Mobile only */}
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-red-50">
        <h2 className="text-2xl font-semibold mb-2 text-red-900">Slot Booking - No Changes Allowed</h2>
        <p className="text-gray-700 text-xs">
          Slot bookings can only be changed by your practice manager. Please contact them to make changes to this
          booking.
        </p>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1">
        {/* Left column - Information */}
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-red-50">
          <h2 className="text-3xl font-semibold mb-4 text-red-900">Slot Booking - No Changes Allowed</h2>
          <p className="mb-2 text-gray-700 text-sm">
            This is a slot booking, which forms part of your regular weekly schedule. Only your practice manager can
            make changes to slot bookings to ensure consistency across the booking system.
          </p>
          <p className="mb-4 text-gray-700 text-sm">
            If you need to make a change to this booking, please contact your practice manager using the details
            provided.
          </p>
        </div>

        {/* Right column - Practice Manager Contact */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm">
          <div className="flex flex-col gap-4">
            {practiceManager ? (
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-indigo-600" />
                  Your Practice Manager
                </h3>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium text-gray-900">
                        {practiceManager.firstName} {practiceManager.lastName}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <a
                        href={`mailto:${practiceManager.email}`}
                        className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                      >
                        {practiceManager.email}
                      </a>
                    </div>
                  </div>

                  {/* Placeholder for phone if it's added to Member type later */}
                  {/* <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{practiceManager.phone}</p>
                    </div>
                  </div> */}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    <strong>Location:</strong> {booking.location?.name || "Unknown Location"}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Room:</strong> {booking.room?.name || "Unknown Room"}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4">Practice Manager Contact</h3>
                <p className="text-gray-600">
                  Practice manager contact information is not available. Please contact your location's administration
                  for assistance.
                </p>
              </div>
            )}

            <div className="flex flex-col gap-2 mt-6">
              <Button variant="ghost" className="w-full text-gray-600" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile content area */}
      <div className="lg:hidden flex-1 bg-gray-50 p-6">
        <div className="flex flex-col gap-4">
          {practiceManager ? (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-indigo-600" />
                Your Practice Manager
              </h3>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium text-gray-900">
                      {practiceManager.firstName} {practiceManager.lastName}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <a
                      href={`mailto:${practiceManager.email}`}
                      className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                    >
                      {practiceManager.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <strong>Location:</strong> {booking.location?.name || "Unknown Location"}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Room:</strong> {booking.room?.name || "Unknown Room"}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">Practice Manager Contact</h3>
              <p className="text-gray-600">
                Practice manager contact information is not available. Please contact your location's administration for
                assistance.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2 mt-6">
            <Button variant="ghost" className="w-full text-gray-600" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlotBookingRestriction;
